import EmptySection from '@/components/EmptySection';
import FilterDropdown from '@/components/FilterDropdown';
import GoBackMenu from '@/components/GoBackNavbar';
import PeopleSection from '@/components/PeopleSection';
import StudentViewSection from '@/components/StudentViewSection';
import Constants from '@/Constants';
import { useContentLanguageChange } from '@/hooks/useContentLanguageChange';
import { useSystemLanguageChange } from '@/hooks/useSystemLanguageChange';
import { LanguageType } from '@/models/LanguageType';
import Option from '@/models/Option';
import { getViewTypeData, ViewType } from '@/models/ViewType';
import { ApplicationState } from '@/store';
import { contentsSearchedClear } from '@/store/item/action';
import { coursesSearchedClear } from '@/store/list/action';
import { programSearchedClear } from '@/store/program/action';
import { searchChange, searchSubmit } from '@/store/search/action';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import _ from 'underscore';
import './style.scss';

const AVAILABLE_LANGUAGES: LanguageType[] = [Constants.language.PT as LanguageType, Constants.language.EN as LanguageType];

const SearchResultPage: React.FC = () => {
    const dispatch = useDispatch();

    const { doSearch, term, lastTerm, strings, availableContentLanguages } = useSelector((state: ApplicationState) => {
        return {
            doSearch: state.search.doSearch,
            term: state.search.data.term,
            lastTerm: state.search.data.lastTerm,
            strings: state.locale.data,
            availableContentLanguages: state.locale.availableContentLanguages,
        };
    });
    const [searchReady, setSearchReady] = useState<boolean>(true);
    const [title, setTitle] = useState<string>('');
    const [hasPerson, setHasPerson] = useState<boolean>(false);
    const [selectedContentLanguages, setSelectedContentLanguages] = useState<Option[]>([]);

    const filterRef = useRef<React.ElementRef<typeof FilterDropdown>>(null);

    const noResult = useSelector(
        (state: ApplicationState) =>
            !hasPerson &&
            !state.lists.listData.listSearchResultStudentView.loading &&
            !state.itens.listData.listSearchResultStudentView.loading &&
            !state.program.listData.listSearchResultStudentView.loading &&
            state.lists.listData.listSearchResultStudentView.total === 0 &&
            state.itens.listData.listSearchResultStudentView.total === 0 &&
            state.program.listData.listSearchResultStudentView.total === 0,
    );

    const showFilter: boolean = AVAILABLE_LANGUAGES.every(
        (language: LanguageType) =>
            !!availableContentLanguages.find((availableContentLanguage: LanguageType) => availableContentLanguage === language),
    );

    const clearSections = () => {
        dispatch(coursesSearchedClear());
        dispatch(contentsSearchedClear());
        dispatch(programSearchedClear());
    };

    const clearResults = () => {
        clearSections();
        setHasPerson(false);
    };

    useEffect(() => {
        updateTitle();

        if (!doSearch && term !== lastTerm) {
            if (term.trim() === '') {
                dispatch(searchChange(lastTerm));
            }
            dispatch(searchSubmit());
        }
    }, [dispatch]);

    useEffect(() => {
        if (doSearch) {
            updateSearch();
        }
    }, [dispatch, doSearch]);

    useEffect(() => {
        if (!searchReady) {
            setSearchReady(true);
        }
    }, [searchReady]);

    const updateTitle = () => {
        setTitle(`${getViewTypeData('listSearchResultStudentView').title} "${term.trim() === '' ? lastTerm : term}"`);
    };

    const updateSearch = () => {
        updateTitle();
        clearResults();
        setSearchReady(false);
    };

    useEffect(() => {
        return () => {
            clearResults();
        };
    }, []);

    useEffect(() => {
        clearSections();
    }, [selectedContentLanguages.length]);

    useEffect(() => {
        if (!showFilter) {
            setSelectedContentLanguages([]);
        }
    }, [showFilter]);

    useContentLanguageChange({ onChange: () => updateSearch() });

    useSystemLanguageChange({
        onChange: () => {
            setSelectedContentLanguages(
                options.filter((option: Option) => !!selectedContentLanguages.find((selectedOption: Option) => selectedOption.key === option.key)),
            );

            updateSearch();
        },
    });

    const urlSearchParam = new URLSearchParams(useLocation().search);

    const goBackPath = urlSearchParam.get('fromProgram') !== null ? '/' : undefined;

    const options = useMemo(() => {
        return [
            {
                key: Constants.language.EN,
                value: strings.LANGUAGE_EN,
            },
            {
                key: Constants.language.PT,
                value: strings.LANGUAGE_PT,
            },
        ];
    }, [strings.LANGUAGE_EN, strings.LANGUAGE_PT]);

    const changeSelectedContentOptionLang = useCallback((selectedOptions: Option[]) => {
        setSelectedContentLanguages(selectedOptions);
    }, []);

    const removeSelectedContentOptionLang = useCallback((option?: Option) => {
        filterRef?.current?.clearFilterTag(option);
    }, []);

    const getLocales = useMemo(() => {
        return _.isEmpty(selectedContentLanguages) ? undefined : selectedContentLanguages.map((lang) => lang.key).join(',');
    }, [selectedContentLanguages]);

    const renderFilterSection = () => {
        return (
            <div>
                {showFilter && (
                    <FilterDropdown
                        label={strings.LANGUAGE}
                        onSelectOption={changeSelectedContentOptionLang}
                        options={options}
                        ref={filterRef}
                        selectedOptionsKey={selectedContentLanguages}
                    />
                )}
            </div>
        );
    };

    const renderSelectedFilterOptions = () => {
        return (
            <>
                {showFilter && (
                    <div className="container">
                        {selectedContentLanguages.length > 0 && (
                            <div className="filter-tags-container">
                                {selectedContentLanguages.map((option) => (
                                    <button className="btn btn-tag" key={option.key} onClick={() => removeSelectedContentOptionLang(option)}>
                                        <span className="tag-title">{option.value}</span>
                                        <i className="tag-icon material-icons" onClick={() => {}}>
                                            {Constants.icons.CLOSE}
                                        </i>
                                    </button>
                                ))}
                                <button className="btn btn-clear-filter-tag" onClick={() => removeSelectedContentOptionLang(undefined)}>
                                    {strings.CLEAR}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            <GoBackMenu goBackPath={goBackPath} />
            <div className="search-result-page">
                {searchReady && (
                    <>
                        <div className="container header-container">
                            <h2 className="title">{title}</h2>
                            {!hasPerson && renderFilterSection()}
                        </div>
                        {!hasPerson && renderSelectedFilterOptions()}

                        <div className="people-view-section">{<PeopleSection onFinishSearch={setHasPerson} />}</div>

                        {hasPerson && (
                            <>
                                <div className="container student-header-container">{renderFilterSection()}</div>
                                {renderSelectedFilterOptions()}
                            </>
                        )}
                        <div>
                            <StudentViewSection hideTitle localesToFilter={getLocales} viewType={'listSearchResultStudentView' as ViewType} />
                        </div>
                    </>
                )}

                {searchReady && noResult && (
                    <div className="empty-container">
                        <EmptySection iconName={Constants.icons.SMS_FAILED} message={strings.NO_RESULTS_FOUND} />
                    </div>
                )}
            </div>
        </>
    );
};

export default SearchResultPage;
